import { useEffect, useState } from 'react';

// Lightweight in-memory notification store. Real-time events (SignalR) push
// into it; the bell subscribes via useNotifications(). Deduped by key so the
// same event can't notify twice.
export type NotifKind = 'new' | 'assigned' | 'escalated' | 'review';
export interface Notif {
  key: string;
  kind: NotifKind;
  title: string;
  senderId: string;
  threadKey?: number;
  ts: number;
  read: boolean;
}

const MAX_ITEMS = 50;
// Dedup keys we've already notified for. Bounded so it can't grow forever:
// we keep at least as many keys as items we retain (a key for any live item
// must stay), with a margin so very-recent-but-evicted items still dedup.
const MAX_SEEN = MAX_ITEMS * 4;

let items: Notif[] = [];
const seen = new Set<string>();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function pushNotification(n: { key: string; kind: NotifKind; title: string; senderId: string; threadKey?: number }) {
  if (seen.has(n.key)) return;
  seen.add(n.key);
  // FIFO eviction by insertion order (Set iterates oldest-first) so `seen`
  // stays bounded while recent keys still dedup.
  while (seen.size > MAX_SEEN) {
    const oldest = seen.values().next().value;
    if (oldest === undefined) break;
    seen.delete(oldest);
  }
  items = [{ ...n, ts: Date.now(), read: false }, ...items].slice(0, MAX_ITEMS);
  emit();
}

// Ask the matching ConversationCard to expand the conversation and open the
// specific sub-thread, then scroll itself into view. Cards listen for this.
export function focusConversation(senderId: string, threadKey?: number) {
  window.dispatchEvent(new CustomEvent('sl-focus-convo', { detail: { senderId, threadKey } }));
}

export function markAllRead() {
  items = items.map((i) => ({ ...i, read: true }));
  emit();
}

export function useNotifications() {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((x) => x + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return { items, unread: items.filter((i) => !i.read).length };
}
